/**
 * Authentication service for the frontend
 * Handles authentication with Django backend using ViewSet-based API.
 *
 * Token storage strategy: HTTPONLY COOKIES
 * ---------------------------------------------------------------------------
 * The access token (oauth2_at) and refresh token (oauth2_rt) are stored
 * exclusively in HttpOnly cookies set by the backend.  JavaScript cannot
 * read or write those cookies, which prevents XSS-based token theft.
 *
 * This service stores only non-sensitive metadata in localStorage:
 *   - auth_expires  — Unix timestamp when the access token expires
 *   - auth_user     — serialised user object (public profile data)
 */

// Import getBaseUrl directly to avoid circular dependency with api.js
import { getBaseUrl } from './api';
import type { ApiRecord, AuthSession } from './types';

// localStorage keys — NO token values are stored here.
const USER_KEY = 'auth_user';
const EXPIRES_KEY = 'auth_expires';

// Shared in-flight refresh promise. The backend rotates refresh tokens
// (single-use, deleted immediately on use), so if two callers each fired
// their own refreshToken() request near-simultaneously, the second one would
// present an already-consumed refresh token and get a 401 — clearing the
// session and forcing a spurious logout even though the first refresh
// succeeded. Concurrent callers instead await this one shared request.
let inFlightRefresh: Promise<number> | null = null;

interface AuthApiResponse extends AuthSession {
  error?: string;
}

const getCsrfFromCookie = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') return value;
  }
  return null;
};

const ensureAuthCsrfToken = async (): Promise<string | null> => {
  let token = getCsrfFromCookie();
  if (token) return token;

  try {
    const csrfUrl = `${getBaseUrl()}/accounts/api/auth/csrf/`;
    await fetch(csrfUrl, {
      method: 'GET',
      credentials: 'include',
    });
  } catch (error) {
    console.warn('Failed to bootstrap CSRF token for auth request:', error);
  }

  return getCsrfFromCookie();
};

/**
 * Authentication service
 */
export const authService = {
  /**
   * Login with username and password
   * @param {string} username
   * @param {string} password
   * @returns {Promise<Object>} { expires, user }
   */
  async login(username: string, password: string): Promise<AuthSession> {
    try {
      const url = `${getBaseUrl()}/accounts/api/auth/login/`;
      const csrfToken = await ensureAuthCsrfToken();

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
        },
        body: JSON.stringify({ username, password }),
        // credentials: 'include' is required so the browser accepts and
        // stores the HttpOnly Set-Cookie headers from the response.
        credentials: 'include',
      });

      if (!response.ok) {
        let errorData: { error?: string };
        try {
          errorData = await response.json();
        } catch (e) {
          throw new Error(`Login failed with status: ${response.status}`);
        }
        throw new Error(errorData.error || 'Login failed');
      }

      const data = await response.json() as AuthApiResponse;

      // Store only non-sensitive metadata (no tokens).
      const authData = {
        expires: data.expires,
        user: data.user,
      };

      this.setSession(authData);
      return authData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  /**
   * Logout the current user
   * @returns {Promise<void>}
   */
  async logout(): Promise<void> {
    try {
      const url = `${getBaseUrl()}/accounts/api/auth/logout/`;
      const csrfToken = await ensureAuthCsrfToken();

      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
        },
        // Send cookies so the server can read and revoke the access-token
        // cookie, and so it receives the CSRF cookie.
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local metadata regardless of API call success.
      this.clearSession();
    }
  },

  /**
   * Refresh the access token.
   *
   * The refresh token travels exclusively in the HttpOnly oauth2_rt cookie.
   * No request body is sent — the backend reads the cookie directly.
   *
   * @returns {Promise<number>} New expiry timestamp
   */
  async refreshToken(): Promise<number> {
    // Concurrent callers share the same in-flight request rather than each
    // consuming/rotating the single-use refresh token themselves — see the
    // comment on `inFlightRefresh` above.
    if (inFlightRefresh) return inFlightRefresh;

    inFlightRefresh = (async () => {
      try {
        const url = `${getBaseUrl()}/accounts/api/auth/refresh_token/`;
        const csrfToken = await ensureAuthCsrfToken();

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
          },
          // Sends the HttpOnly oauth2_rt cookie to the refresh endpoint.
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Token refresh failed with status: ${response.status}`);
        }

        const data = await response.json() as AuthApiResponse;

        // Update local metadata with the new expiry.
        this.setSession({ expires: data.expires, user: data.user });

        return data.expires;
      } catch (error) {
        console.error('Token refresh error:', error);
        // Clear metadata if the refresh token is also gone / expired.
        this.clearSession();
        throw error;
      } finally {
        inFlightRefresh = null;
      }
    })();

    return inFlightRefresh;
  },

  /**
   * Get the current user's information
   * @param {boolean} forceFetch - Force fetch from server even if cached data exists
   * @returns {Promise<Object>} User data
   */
  async getCurrentUser(forceFetch = false): Promise<ApiRecord> {
    try {
      if (!forceFetch) {
        const userData = this.getUser();
        if (userData) {
          return userData;
        }
      }

      const url = `${getBaseUrl()}/accounts/api/auth/me/`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        // Sends the HttpOnly oauth2_at cookie so the server can authenticate.
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to get user info with status: ${response.status}`);
      }

      const data = await response.json() as ApiRecord;

      // Cache user data locally.
      localStorage.setItem(USER_KEY, JSON.stringify(data));

      return data;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  },

  /**
   * Store non-sensitive session metadata (expiry + user data).
   * Token values are never stored here — they live in HttpOnly cookies.
   * @param {Object} authData — may contain { expires, user }
   */
  setSession(authData: Partial<AuthSession>) {
    if (authData.expires) {
      localStorage.setItem(EXPIRES_KEY, authData.expires.toString());
    }

    if (authData.user) {
      localStorage.setItem(USER_KEY, JSON.stringify(authData.user));
    }
  },

  /**
   * Clear all local session metadata.
   * The HttpOnly cookies are cleared server-side by the logout endpoint.
   */
  clearSession() {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(EXPIRES_KEY);
  },

  /**
   * Check if the user is likely still authenticated.
   *
   * Since tokens are HttpOnly, we can only check the locally stored expiry
   * timestamp as a client-side hint.  The server is the authoritative source —
   * a 401 response clears the session regardless of this flag.
   *
   * @returns {boolean}
   */
  isAuthenticated() {
    const expires = this.getTokenExpiry();
    if (!expires) {
      return false;
    }
    const now = new Date().getTime() / 1000;
    return now < expires;
  },

  /**
   * Get the token expiry timestamp.
   * @returns {number|null}
   */
  getTokenExpiry(): number | null {
    const expires = localStorage.getItem(EXPIRES_KEY);
    return expires ? parseFloat(expires) : null;
  },

  /**
   * Get the stored user data.
   * @returns {Object|null}
   */
  getUser(): ApiRecord | null {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) as ApiRecord : null;
  },
};
